import { createContext, useCallback, useMemo, useRef, useState } from 'react'
import { campaignQuickSearchUrl, fetchDashboardDetails, analyticsQueryUrl } from 'src/configs/api'
import { useAPI } from 'src/utils/api'

const defaultProvider = {
    campaignDashboardDetails: [],
    setCampaignDashboardDetails: () => null,

    eventsCountsDetails: [],
    setEventCountsDetails: () => null,
    eventGraphDetails: [],
    setEventGraphDetails: () => null,
    browserTypeDetails: [],
    setBrowserTypeDetails: () => null,
    deviceTypeDetails: [],
    setDeviceTypeDetails: () => null,

    campaignQuickSearchResults: [],
    setCampaignQuickSearchResults: () => null,
    campaignSearchLoading: false,
    setCampaignSearchLoading: () => null,
    searchCampaigns: async () => null,
    fetchAnalyticsMetrics: async () => null,
}

const CampaignAudienceDashboardContext = createContext(defaultProvider)

const CampaignAudienceDashboardProvider = ({ children }) => {
    const [campaignDashboardDetails, setCampaignDashboardDetails] = useState(defaultProvider.campaignDashboardDetails)
    const [eventsCountsDetails, setEventCountsDetails] = useState(defaultProvider.eventsCountsDetails)
    const [eventGraphDetails, setEventGraphDetails] = useState(defaultProvider.eventGraphDetails)
    const [browserTypeDetails, setBrowserTypeDetails] = useState(defaultProvider.browserTypeDetails)
    const [deviceTypeDetails, setDeviceTypeDetails] = useState(defaultProvider.deviceTypeDetails)
    const [campaignQuickSearchResults, setCampaignQuickSearchResults] = useState(defaultProvider.campaignQuickSearchResults)
    const [campaignSearchLoading, setCampaignSearchLoading] = useState(false)

    // Track the latest request to ignore stale responses
    const searchRequestIdRef = useRef(0)

    const api = useAPI()

    const getCampaignDashboardDetails = useCallback(async () => {
        try {
            const response = await api.post(`${fetchDashboardDetails}/campaign-metrics`)
            if (response?.status === 200) {
                const detailedData = response?.data?.data
                setEventCountsDetails(detailedData?.event_counts)
                setEventGraphDetails(detailedData?.event_graph)
                setBrowserTypeDetails(detailedData?.browser_types)
                setDeviceTypeDetails(detailedData?.device_types)
            }
        } catch {
            // handled by api interceptor
        }
    }, [api])

    const getSingleCampaignDashboardDetails = useCallback(async () => {
        try {
            const inputData = {
                "campaign_id": "019d2e11-bd8e-7996-9b4b-d2626d1cefde",
                "channel_ids": [
                    "e542c76e-3b7a-4952-b6d3-1ae2142139c3",
                    "a1b2c3d4-5678-9012-abcd-ef1234567890"
                ],
                "start_date": "2026-02-18",
                "end_date": "2026-03-30"
            }
            await api.post(`${fetchDashboardDetails}/campaign-metrics`, inputData)

        } catch {
            // handled by api interceptor
        }
    }, [api])

    /**
     * Search campaigns via quick-search API.
     * @param {string} [query] - Search keyword. Omit or pass empty string for "recents" mode.
     */
    const searchCampaigns = useCallback(async (query = '', type = 'all') => {
        const requestId = ++searchRequestIdRef.current
        setCampaignSearchLoading(true)

        try {
            const params = { limit: 10 }
            if (query && query.trim()) params.query = query.trim()
            if (type && type !== 'all') params.type = type

            const response = await api.get(campaignQuickSearchUrl, { params })

            // Discard stale response if a newer request has fired
            if (requestId !== searchRequestIdRef.current) return

            if (response?.status === 200) {
                const results = response?.data?.data?.results || []
                setCampaignQuickSearchResults(results)
            }
        } catch {
            if (requestId !== searchRequestIdRef.current) return
            setCampaignQuickSearchResults([])
        } finally {
            if (requestId === searchRequestIdRef.current) {
                setCampaignSearchLoading(false)
            }
        }
    }, [api])

    const fetchAnalyticsMetrics = useCallback(async (payload) => {
        try {
            const response = await api.post(analyticsQueryUrl, payload)
            if (response?.status === 200) {
                // Handle both { data: { rows, columns } } and { rows, columns } envelopes
                const result = response?.data?.data ?? response?.data ?? null
                return result?.rows != null ? result : null
            }
            return null
        } catch {
            return null
        }
    }, [api])

    const values = useMemo(() => ({
        getCampaignDashboardDetails,
        campaignDashboardDetails,
        setCampaignDashboardDetails,
        getSingleCampaignDashboardDetails,
        eventsCountsDetails,
        setEventCountsDetails,
        eventGraphDetails,
        setEventGraphDetails,
        browserTypeDetails,
        setBrowserTypeDetails,
        deviceTypeDetails,
        setDeviceTypeDetails,
        campaignQuickSearchResults,
        setCampaignQuickSearchResults,
        campaignSearchLoading,
        searchCampaigns,
        fetchAnalyticsMetrics,
    }), [
        getCampaignDashboardDetails,
        campaignDashboardDetails,
        getSingleCampaignDashboardDetails,
        eventsCountsDetails,
        eventGraphDetails,
        browserTypeDetails,
        deviceTypeDetails,
        campaignQuickSearchResults,
        campaignSearchLoading,
        searchCampaigns,
        fetchAnalyticsMetrics,
    ])

    return <CampaignAudienceDashboardContext.Provider value={values}>{children}</CampaignAudienceDashboardContext.Provider>
}

export { CampaignAudienceDashboardContext, CampaignAudienceDashboardProvider }
